/**
 * Validation rules shared by the client and the server.
 *
 * The client uses these so a person gets instant feedback in a form. The server
 * uses the *same functions* so the rules cannot drift — but the server always
 * re-runs them, because client-side validation is a convenience, never a check.
 */
export const LIMITS = {
    emailMax: 254,
    passwordMin: 12,
    passwordMax: 200,
    displayNameMin: 3,
    displayNameMax: 24,
    characterNameMin: 3,
    characterNameMax: 20
};
/**
 * Deliberately permissive. Over-strict email regexes reject valid addresses;
 * the real proof that an address works is the verification email.
 */
const EMAIL_RE = /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/;
/** Letters, digits, single spaces, hyphen and apostrophe. No leading/trailing space. */
const DISPLAY_NAME_RE = /^[\p{L}\p{N}]+(?:[ '-][\p{L}\p{N}]+)*$/u;
/** Character names are tighter — no spaces, so chat and /whisper stay unambiguous. */
const CHARACTER_NAME_RE = /^[\p{L}][\p{L}\p{N}'-]*$/u;
export function validateEmail(value) {
    if (typeof value !== 'string')
        return 'Enter an email address.';
    const v = value.trim();
    if (!v)
        return 'Enter an email address.';
    if (v.length > LIMITS.emailMax)
        return 'That email address is too long.';
    if (!EMAIL_RE.test(v))
        return 'That does not look like an email address.';
    return null;
}
/**
 * Length-first, not a character-class obstacle course. A 12-character
 * passphrase beats "Pa55word!" and people can actually remember it.
 * We also refuse the handful of strings that dominate breach lists.
 */
const OBVIOUS_PASSWORDS = new Set([
    'password', 'passw0rd', 'password1', 'password123', 'letmein', 'welcome',
    'qwertyuiop', 'iloveyou', 'admin', 'changeme', '123456789012', 'emberreach'
]);
export function validatePassword(value) {
    if (typeof value !== 'string')
        return 'Enter a password.';
    if (value.length < LIMITS.passwordMin) {
        return `Use at least ${LIMITS.passwordMin} characters. A few words together works well.`;
    }
    if (value.length > LIMITS.passwordMax)
        return 'That password is too long.';
    const squashed = value.toLowerCase().replace(/\s+/g, '');
    if (OBVIOUS_PASSWORDS.has(squashed))
        return 'That password is too easy to guess.';
    if (/^(.)\1+$/.test(value))
        return 'That password is too easy to guess.';
    return null;
}
export function validateDisplayName(value) {
    if (typeof value !== 'string')
        return 'Enter a display name.';
    const v = value.trim();
    if (v.length < LIMITS.displayNameMin) {
        return `Display names need at least ${LIMITS.displayNameMin} characters.`;
    }
    if (v.length > LIMITS.displayNameMax) {
        return `Display names can be at most ${LIMITS.displayNameMax} characters.`;
    }
    if (!DISPLAY_NAME_RE.test(v)) {
        return 'Use letters and numbers, with single spaces, hyphens or apostrophes between them.';
    }
    return null;
}
export function validateCharacterName(value) {
    if (typeof value !== 'string')
        return 'Enter a character name.';
    const v = value.trim();
    if (v.length < LIMITS.characterNameMin) {
        return `Character names need at least ${LIMITS.characterNameMin} characters.`;
    }
    if (v.length > LIMITS.characterNameMax) {
        return `Character names can be at most ${LIMITS.characterNameMax} characters.`;
    }
    if (!CHARACTER_NAME_RE.test(v)) {
        return 'Start with a letter, then letters, numbers, hyphens or apostrophes. No spaces.';
    }
    return null;
}
/** Collects field errors, skipping the ones that passed. */
export function collectErrors(checks) {
    const out = [];
    for (const [field, message] of checks) {
        if (message !== null)
            out.push({ field, message });
    }
    return out;
}
/** Case-insensitive uniqueness key for emails. Store alongside the display form. */
export function normaliseEmail(email) {
    return email.trim().toLowerCase();
}
//# sourceMappingURL=validation.js.map